import { UIIcon } from '../config/icons';
import { ABFilter, ABFilterBar } from '../lib/ab/filter-bar';
import { ABObjectCount } from '../lib/ab/widgets-admin';
import { c_cities } from '../lib/mdl/c-city';
import { c_status_name, cUserAccountStatus } from '../lib/mdl/c-user';
import { CUserAdminListView, cUserCalcLoyalty, CUserCommentsView, cUsersAdminListQuery, cUsersCommentsView, cUsersSuspiciousView, CUserSuspiciousView } from '../lib/mdl/c-user-admin';
import { makeActionButton } from '../lib/ui/component/button';
import { UITableDefinition, UITable } from '../lib/ui/component/table';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIViewSpec } from '../lib/ui/view';
import { QByFilter, YQueryDataSource, YArrayDataSource } from '../lib/y/data-source';
import { $log, xavis_experiments } from '../lib/y/debug';
import { $fn } from '../lib/y/fn';
import { $location } from '../lib/y/location';
import { YTime, $time } from '../lib/y/time';
import { YId } from '../lib/y/types';

function getUserQueryFilter(filters: ABFilter[], search: string) {
    const filter: QByFilter = { by: {} };

    for(const f of filters) {
        if(f.key == 'city')
            filter.by.city = { in: f.value.map((s: YId) => ({ str: s })) };
        else if(f.key == 'is_test')
            filter.by.is_test = { eq: { bool: f.value.includes('true') } };
        else if(f.key == 'is_admin')
            filter.by.is_admin = { eq: { bool: f.value.includes('true') } };
        else if(f.key == 'plan' && f.value.length)
            filter.by.plan = { eq: { str: f.value[0] } };
        else if(f.key == 'account_status' && f.value.length)
            filter.by.account_status = { in: f.value.map((s: YId) => ({ str: s })) };
    }

    search = search.trim();
    if(search.length) {
        filter.by.search = { eq: { str: search } };
    }

    return filter;
}

export class AdminUsersScreen extends UIScreen {
    protected override fillContent(content: UIViewSpec): void {
        const doSearch = () => {
            source.filter = getUserQueryFilter(filter_bar.filters.filters, filter_bar.search_input!.value);
        }

        const count = new ABObjectCount('users');

        const filter_bar = new ABFilterBar({
            on_search: $fn.debounce(doSearch, 0.3),
            on_filter: doSearch,
            right_content: [count],
            no_header: true
        });

        const filters = filter_bar.filters;
        filters.addFilterButton();
        filters.addCity(true);
        filters.addFullStatus();
        filters.addPlan();
        filters.addIsTest();
        filters.addIsAdmin();

        const def = new UITableDefinition<CUserAdminListView>();
        def.addValue(u => u.first_name + ' ' + u.last_name, { id: 'name', title: 'Name', width: 160 });
        def.addValue(u => u.email ?? '-', { id: 'email', title: 'Email', width: 160, optional: true });
        def.addValue(u => u.phone ?? '-', { id: 'phone', title: 'Phone', width: 160, optional: true });
        def.addValue(u => c_cities.maybe(u.city_id)!.name, { id: 'city', title: 'City', width: -120, optional: true });
        def.addValue(u => u.plan, { id: 'plan', title: 'Plan', width: -120, optional: true });
        def.addValue(u => !!Object.keys(u.permissions).length, $fmt.CHECKMARK, { title: 'Is admin', width: -80, optional: true });
        def.addValue(u => c_status_name[cUserAccountStatus(u)], { id: 'status', title: 'Status', width: -160, optional: true });
        def.addValue(u => u.statistics.revenue, $fmt.CURRENCY, { id: 'revenue', title: 'Total revenue', width: -160, optional: true });
        def.addValue(u => u.statistics.ticket_value, $fmt.CURRENCY, { id: 'ticket_value', title: 'Ticket value', width: -160, optional: true });
        def.addValue(u => u.statistics.payout_value, $fmt.CURRENCY, { id: 'payout_value', title: 'Payout value', width: -160, optional: true });
        def.addValue(u => u.statistics.bookings_used, $fmt.NUMBER, { id: 'bookings_used', title: 'Bookings used', width: -120, optional: true });
        def.addValue(u => u.statistics.pause_count, $fmt.NUMBER, { id: 'pause_count', title: 'Pauses', width: -120, optional: true });
        def.addValue(u => Math.max(0, u.statistics.subscribe_count - 1), $fmt.NUMBER, { id: 'subscribe_count', title: 'Resubscribes', width: -120, optional: true });
        def.addValue(u => cUserCalcLoyalty(u), $fmt.NUMBER, { id: 'loyalty', title: 'Loyalty', width: -120, optional: true });

        def.addButtons([
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                    $location.push('user', { user_id: item.id });
                }
            },
            { icon: UIIcon.EYE, tooltip: 'View as user', link: u => `https://abundolive.se?as_user=${u.as_user_token}` }
        ], { width: -62 });

        def.preference_prefix = 'users';

        const source = new YQueryDataSource(params => cUsersAdminListQuery(params).then(response => {
            if(response.count) {
                if(response.count == 1001)
                    count.text = '1000+';
                else
                    count.number = response.count;
            }
            return response;
        }));

        if(!xavis_experiments)
            filters.add('is_test', 'Is test', ['false'], 'No test');

        const table = new UITable(def, source);

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block m-widest', [
                filter_bar,
                table,
            ])),
            makeActionButton(UIIcon.PLUS, 'Create new', () => { $location.push('user') })
        ]);

        table.on_row_selected = (r) => {
            $log('app', r.item.id);
            r.expand(true);
        }

        this.onViewDidReappear = () => { source.refresh() };
    }
}

export class AdminCommentsScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        const all_comments = await cUsersCommentsView();

        const doSearch = () => {
            let comments = all_comments;

            let from: YTime | undefined;
            let to: YTime | undefined;

            for(const f of filters.filters) {
                switch(f.key) {
                    case 'city': comments = comments.filter(u => f.value.includes(u.city_id)); break;
                    case 'from_date': from = f.value.start; break;
                    case 'to_date': to = f.value.end; break;
                }
            }

            if(from || to)
                comments = comments.filter(c => $time(c.date).inRange(from, to));

            comments = filter_bar.search_input!.filterArray(comments, (c, w) => {
                if(c.first_name.toLowerCase().includes(w)) return true;
                if(c.last_name.toLowerCase().includes(w)) return true;
                if(c.email.toLowerCase().includes(w)) return true;
                if(c.notes.toLowerCase().includes(w)) return true;
                if(c_cities.maybe(c.city_id)!.name.toLowerCase().includes(w)) return true;
                return false;
            });

            count.number = comments.length;
            source.data = comments;
        }

        const count = new ABObjectCount('comments');

        const filter_bar = new ABFilterBar({
            on_search: doSearch,
            on_filter: doSearch,
            right_content: [count],
            no_header: true
        });

        const filters = filter_bar.filters;
        filters.addFilterButton();
        filters.addCity(true);
        filters.addFromDate();
        filters.addToDate();

        const def = new UITableDefinition<CUserCommentsView>();
        def.addValue(e => e.email, {
            title: 'Email', width: 100, classes: 'u-clickable', on_click: row => {
                $location.push('user', { user_id: row.item.id });
            }
        });

        def.addValue(e => e.notes, { title: 'Comment', width: 300 });
        def.addValue(e => $time(e.date), $fmt.DATE('dd-MM-yyyy'), { title: 'Cancel date', width: -120 });
        def.addValue(e => $time(e.effective_date), $fmt.DATE('dd-MM-yyyy'), { title: 'Subscription ends', width: -160 });

        def.preference_prefix = 'comments';

        const source = new YArrayDataSource<CUserCommentsView>();
        const table = new UITable(def, source);

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                filter_bar,
                table,
            ])),
        ]);

        doSearch();
    }
}

class FilteredSuspiciousScreen extends UIScreen {
    constructor(private _users: CUserSuspiciousView[]) {
        super();
    }

    protected override fillContent(content: UIViewSpec): void {
        const users = this._users;

        const count = new ABObjectCount('users');
        count.number = users.length;

        const filter_bar = new ABFilterBar({
            right_content: [count],
            no_header: true
        });

        const def = new UITableDefinition<CUserSuspiciousView>();
        def.addValue(u => u.first_name + ' ' + u.last_name, { title: 'Name', width: 160 });
        def.addValue(u => u.email, { title: 'Email', width: 160 });
        def.addValue(u => u.phone, { title: 'Phone', width: 120 });
        def.addValue(u => u.credit_card, { title: 'Credit card', width: 120, classes: 't-ellipsize' });
        def.addValue(u => u.account_status, { title: 'Status', width: -160, sort: u => u.account_status });
        def.addValue(u => $time(u.created_at), $fmt.DATE('dd-MM-yyyy'), { title: 'Created', width: -120 });
        def.addValue(u => u.score, $fmt.NUMBER, { title: 'Score', width: -80 });
        def.addButtons([
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                    $location.push('user', { user_id: item.id });
                }
            }
        ], { width: -60 });

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block m-wide', [
                filter_bar,
                new UITable(def, new YArrayDataSource(users)),
            ])),
        ]);
    }
}

export class AdminSuspiciousScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        let all_users = await cUsersSuspiciousView();

        const doSearch = () => {
            let users = all_users;

            let from: YTime | undefined;
            let to: YTime | undefined;

            for(const f of filters.filters) {
                if(f.key == 'from_date')
                    from = f.value.start;
                else if(f.key == 'to_date')
                    to = f.value.end;
            }

            if(from || to)
                users = users.filter(u => $time(u.created_at).inRange(from, to));

            users = filter_bar.search_input!.filterArray(users, (u, w) => {
                if(u.first_name.toLowerCase().includes(w)) return true;
                if(u.last_name.toLowerCase().includes(w)) return true;
                if(u.email.toLowerCase().includes(w)) return true;
                if(u.account_status.toLowerCase().includes(w)) return true;
                if(u.phone.includes(w)) return true;
                if(u.credit_card.includes(w)) return true;
                return false;
            });

            count.number = users.length;
            source.data = users;
        }

        const doRefresh = async () => {
            all_users = await cUsersSuspiciousView();
            doSearch();
        }

        const count = new ABObjectCount('users');

        const filter_bar = new ABFilterBar({
            on_search: doSearch,
            on_filter: doSearch,
            on_refresh: doRefresh,
            right_content: [count],
            no_header: true
        });

        const filters = filter_bar.filters;
        filters.addFilterButton();
        filters.addFromDate();
        filters.addToDate();

        const def = new UITableDefinition<CUserSuspiciousView>();
        def.addValue(u => u.first_name + ' ' + u.last_name, { title: 'Name', width: 160 });
        def.addValue(u => u.email, { title: 'Email', width: 160 });
        def.addValue(u => u.phone, { title: 'Phone', width: 120 });
        def.addValue(u => u.credit_card, { title: 'Credit card', width: 120, classes: 't-ellipsize' });
        def.addValue(u => u.account_status, { title: 'Status', width: -160, sort: u => u.account_status });
        def.addValue(u => $time(u.created_at), $fmt.DATE('dd-MM-yyyy'), { title: 'Created', width: -120 });
        def.addValue(u => u.score, $fmt.NUMBER, { title: 'Score', width: -80 });
        def.addButtons([
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                    $location.push('user', { user_id: item.id });
                }
            },
            {
                icon: UIIcon.EYE, tooltip: 'Duplicates', action: item => {
                    $location.push({ transient: new FilteredSuspiciousScreen(all_users.filter(u => item.duplicates.includes(u.id))) });
                }
            }
        ], { width: -60 });

        def.preference_prefix = 'users_suspicious';

        const source = new YArrayDataSource<CUserSuspiciousView>();
        const table = new UITable(def, source);

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block m-wider', [
                filter_bar,
                table,
            ])),
        ]);

        doSearch();
    }
}
